import { react2Angular } from "~/Core/React2Angular";
import React, { useState } from "react";
import { NodeRendererProps, Tree } from "@plandelta/react-arborist";
import { SystemFolder, useGetSystemFolders } from "~/Hooks/Api/SystemFolders";
import { Box, Flex, Skeleton } from "@optiaxiom/react";
import { SystemFile, useGetSystemFiles } from "~/Hooks/Api/SystemFiles";
import { DateDisplay } from "~/Components/Common/DateDisplay";
import styles from "~/Components/SystemFilesBrowser.module.css";
import { downloadSystemFile } from "~/Common/DownloadSystemFile";

function humanFileSize(bytes: number, decimalPlaces = 1) {
    let thresh = 1024;

    if (Math.abs(bytes) < thresh) {
        return "1 KB";
    }

    let units = ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    let u = -1;
    let r = 10 ** decimalPlaces;

    do {
        bytes /= thresh;
        ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return bytes.toFixed(decimalPlaces) + " " + units[u];
}

const Node = ({ node, style }: NodeRendererProps<SystemFolder>) => {
    let className = node.isOpen ? "k-minus" : "k-plus";

    return (
        <Flex className={styles.treeitem} style={style} flexDirection="row" alignItems="center" gap="0">
            <span className={"k-icon " + className} onClick={() => node.toggle()}></span>
            <span>{node.data.name}</span>
        </Flex>
    );
};

const SystemFilesBrowser = () => {
    let [selectedPath, setSelectedPath] = useState("_system/");
    let [sort, setSort] = useState<{ property: keyof SystemFile; descending: boolean }>({
        property: "name",
        descending: false,
    });
    let { data: systemFoldersData } = useGetSystemFolders();
    let { data: systemFilesData } = useGetSystemFiles(selectedPath);
    if (!systemFoldersData) {
        return null;
    }

    systemFoldersData = [
        {
            name: "SystemFiles",
            path: "_system/",
            folders: systemFoldersData,
        },
    ];

    let sortByProperty = (property: keyof SystemFile, descending: boolean) => {
        if (!systemFilesData) {
            return undefined;
        }

        return [...systemFilesData].sort((a, b) => {
            const valA = a[property];
            const valB = b[property];

            if (valA < valB) return !descending ? -1 : 1;
            if (valA > valB) return !descending ? 1 : -1;
            return 0;
        });
    };

    let changeSort = (sortProperty: keyof SystemFile) => {
        if (sortProperty === sort.property) {
            setSort({ property: sort.property, descending: !sort.descending });
        } else {
            setSort({ property: sortProperty, descending: false });
        }
    };

    let getSortClass = (property: keyof SystemFile) => {
        if (property !== sort.property) {
            return "";
        }

        return sort.descending ? "st-sort-descent" : "st-sort-ascent";
    };

    let sortedSystemFiles = sortByProperty(sort.property, sort.descending);

    return (
        <Flex flexDirection="row" alignItems="stretch" gap="0" h="full">
            <Box className={styles.treeContainer}>
                <Tree
                    selection={selectedPath}
                    initialData={systemFoldersData}
                    idAccessor="path"
                    childrenAccessor={o => o.folders}
                    disableDrop={true}
                    disableDrag={true}
                    disableEdit={true}
                    disableMultiSelection={true}
                    onActivate={o => setSelectedPath(o.id)}
                >
                    {Node}
                </Tree>
            </Box>
            <Box className={styles.tableContainer}>
                {!sortedSystemFiles && (
                    <Box>
                        <Skeleton h="48" m="2" />
                        <Skeleton h="40" m="2" />
                        <Skeleton h="40" m="2" />
                        <Skeleton h="40" m="2" />
                        <Skeleton h="40" m="2" />
                    </Box>
                )}
                {sortedSystemFiles && (
                    <>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th
                                        className={"text-left " + getSortClass("name")}
                                        onClick={() => changeSort("name")}
                                    >
                                        <div className="column-header">Name</div>
                                    </th>
                                    <th
                                        className={"text-text-right " + getSortClass("size")}
                                        onClick={() => changeSort("size")}
                                        style={{ width: "150px" }}
                                    >
                                        <div className="column-header">Size</div>
                                    </th>
                                    <th
                                        className={"text-left " + getSortClass("modifiedOn")}
                                        onClick={() => changeSort("modifiedOn")}
                                        style={{ width: "250px" }}
                                    >
                                        <div className="column-header">Modified</div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSystemFiles.map(o => (
                                    <tr key={o.name}>
                                        <td>
                                            <a onClick={() => downloadSystemFile(`${o.path}${o.name}`)}>{o.name}</a>
                                        </td>
                                        <td className="text-right">{humanFileSize(o.size)}</td>
                                        <td>
                                            <DateDisplay dateString={o.modifiedOn} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {sortedSystemFiles.length === 0 && <Box m="16">This folder is empty.</Box>}
                    </>
                )}
            </Box>
        </Flex>
    );
};

angular.module("insite-admin").component("systemFilesBrowser", react2Angular(SystemFilesBrowser));
